/**
 * Conversion utilities between the RulePack editor draft model and
 * the persisted PlanDefinition FHIR resource.
 *
 * V1: intake/episode lifecycle automations. Extensions carry
 * execution-ready metadata (actionKey, template refs, recipient policies,
 * dedupe, timing, comm-pref flags) so a Bot can act idempotently.
 */
import type { Extension, PlanDefinition, PlanDefinitionAction } from '@medplum/fhirtypes';

import {
  CURRENT_SCHEMA_VERSION,
  RE_CANONICAL_URL_PREFIX,
  RE_CONFLICT_POLICY_EXTENSION,
  RE_DOMAIN_EXTENSION,
  RE_RUNG_ACTION_EXTENSION,
  RE_RUNG_CONDITION_EXTENSION,
  RE_RUNG_ELSE_BRANCH_EXTENSION,
  RE_SCHEMA_VERSION_EXTENSION,
  RE_SCOPE_EXTENSION,
  RE_STABLE_RULE_KEY_EXTENSION,
  RE_TRIGGER_EXTENSION,
  RE_V1_MARKER_EXTENSION,
  RE_WORKFLOW_STEPS_EXTENSION,
  migrateV1TriggerToV2,
  type ActionScheduledTiming,
  type ActionType,
  type ConditionField,
  type ConditionGroup,
  type ConditionOperator,
  type ConflictPolicy,
  type LadderRung,
  normalizeRecipientPolicy,
  type RecipientPolicy,
  type ResourceFieldFilter,
  type RuleAction,
  type RuleActionKey,
  type RulePackDomain,
  type RulePackDraft,
  type RulePackListItem,
  type RulePackStatus,
  type RulesEngineSchemaVersion,
  type ScopeConfig,
  type TemplateLibraryRef,
  type TimingBucket,
  type TriggerConfig,
  type TriggerEventKind,
  type TriggerFilterKind,
  type TriggerMode,
  type TriggerObjectFilter,
  type TriggerOperation,
  type TriggerResourceType,
  type WorkflowBranch,
  type WorkflowStep,
} from '../types';

let editorIdCounter = 0;
export function makeEditorId(prefix = 're'): string {
  return `${prefix}-${Date.now()}-${++editorIdCounter}`;
}

export function generateStableRuleKey(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return `rk-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

// ---------------------------------------------------------------------------
// PlanDefinition → RulePackDraft
// ---------------------------------------------------------------------------

export function planDefinitionToRulePackDraft(pd: PlanDefinition): RulePackDraft {
  const rawDomain = getExtString(pd.extension, RE_DOMAIN_EXTENSION);
  const domain = migrateDomain(rawDomain);
  const trigger = parseTriggerExtension(pd.extension);
  const scope = parseScopeExtension(pd.extension);
  const conflictPolicy = (getExtString(pd.extension, RE_CONFLICT_POLICY_EXTENSION) || 'all-match') as ConflictPolicy;
  const stableRuleKey = getExtString(pd.extension, RE_STABLE_RULE_KEY_EXTENSION);
  const steps = parseWorkflowStepsExtension(pd.extension);
  const rungs = steps?.length
    ? workflowStepsToRungs(steps)
    : (pd.action ?? []).map(actionToLadderRung);

  const schemaVersionRaw = getExtNumber(pd.extension, RE_SCHEMA_VERSION_EXTENSION);
  const schemaVersion: RulesEngineSchemaVersion = (schemaVersionRaw === 2 ? 2 : 1);

  return {
    id: pd.id,
    stableRuleKey,
    title: pd.title ?? 'Untitled Rule',
    description: pd.description ?? '',
    version: pd.version,
    status: (pd.status as RulePackStatus) || 'draft',
    domain,
    trigger,
    scope,
    conflictPolicy,
    rungs,
    schemaVersion,
    ...(steps ? { steps } : {}),
  };
}

/** Migrate old domains — `client-lifecycle` maps to `client-events` (its general-purpose successor). */
function migrateDomain(raw: string | undefined): RulePackDomain {
  if (raw === 'documents') return 'client-events';
  if (raw === 'client-lifecycle') return 'client-events';
  if (raw === 'client-intake' || raw === 'client-events' || raw === 'billing' || raw === 'claim-assembly') return raw;
  return (raw as RulePackDomain) || 'client-events';
}

function parseTriggerExtension(extensions: Extension[] | undefined): TriggerConfig {
  const ext = findExtension(extensions, RE_TRIGGER_EXTENSION);
  if (!ext?.extension) {
    return { resourceTypes: [], eventKinds: [], mode: 'real-time', manualRunEligible: true, sideEffectsAllowed: false, objectFilters: [] };
  }
  const rawEventKinds = getExtStringArray(ext.extension, 'eventKind');
  const objectFiltersJson = getExtString(ext.extension, 'objectFilters');
  let objectFilters: TriggerObjectFilter[] = [];
  if (objectFiltersJson) {
    try {
      const parsed = JSON.parse(objectFiltersJson);
      if (Array.isArray(parsed)) {
        objectFilters = parsed.map((f: Record<string, unknown>) => ({
          id: (f.id as string) ?? '',
          kind: f.kind as TriggerFilterKind,
          system: f.system as string | undefined,
          code: f.code as string | undefined,
          value: f.value as string | undefined,
          label: f.label as string | undefined,
        }));
      }
    } catch { /* migration: ignore bad JSON */ }
  }

  // Repeated operation extensions are the V2 multi-operation representation.
  // A single existing operation extension remains fully backward compatible.
  const operations = getExtStringArray(ext.extension, 'operation') as TriggerOperation[];
  const operation = operations.length === 1 ? operations[0] : undefined;
  let filters: ResourceFieldFilter[] | undefined;
  const filtersJson = getExtString(ext.extension, 'filters');
  if (filtersJson) {
    try {
      const parsed = JSON.parse(filtersJson);
      if (Array.isArray(parsed)) filters = parsed as ResourceFieldFilter[];
    } catch { /* ignore bad JSON */ }
  }

  const trigger: TriggerConfig = {
    resourceTypes: getExtStringArray(ext.extension, 'resourceType') as TriggerResourceType[],
    eventKinds: rawEventKinds.map(migrateEventKind) as TriggerEventKind[],
    mode: (getExtString(ext.extension, 'mode') || 'real-time') as TriggerMode,
    manualRunEligible: getExtBoolean(ext.extension, 'manualRunEligible') ?? true,
    sideEffectsAllowed: getExtBoolean(ext.extension, 'sideEffectsAllowed') ?? false,
    objectFilters,
    ...(operations.length > 0 ? { operations } : {}),
    ...(operation ? { operation } : {}),
    ...(filters ? { filters } : {}),
  };

  // If no V2 operation/filters found, migrate from V1 event kinds
  if (operations.length === 0 && rawEventKinds.length > 0) {
    return migrateV1TriggerToV2(trigger);
  }

  return trigger;
}

/** Migrate old event kinds to V1 event kinds */
function migrateEventKind(ek: string): string {
  const map: Record<string, string> = {
    'intake-started': 'client-intake-completed',
    'intake-submitted': 'client-intake-completed',
    'intake-incomplete-threshold-reached': 'client-intake-completed',
    'document-request-created': 'client-intake-completed',
    'document-incomplete-threshold-reached': 'client-intake-completed',
    'document-completed': 'client-intake-completed',
    'manual-email-requested': 'manual-rule-run',
  };
  return map[ek] ?? ek;
}

function parseScopeExtension(extensions: Extension[] | undefined): ScopeConfig {
  const ext = findExtension(extensions, RE_SCOPE_EXTENSION);
  if (!ext?.extension) {
    return { practiceIds: [] };
  }
  return {
    practiceIds: getExtStringArray(ext.extension, 'practiceId'),
    effectiveDateStart: getExtString(ext.extension, 'effectiveDateStart') || undefined,
    effectiveDateEnd: getExtString(ext.extension, 'effectiveDateEnd') || undefined,
  };
}

function parseWorkflowStepsExtension(
  extensions: Extension[] | undefined,
): WorkflowStep[] | undefined {
  const ext = findExtension(extensions, RE_WORKFLOW_STEPS_EXTENSION);
  if (!ext?.valueString) return undefined;
  try {
    const parsed = JSON.parse(ext.valueString);
    if (!Array.isArray(parsed)) return undefined;
    return parsed as WorkflowStep[];
  } catch {
    return undefined;
  }
}

function actionToLadderRung(action: PlanDefinitionAction, index: number): LadderRung {
  const conditionGroups = parseConditionGroups(action.extension);
  const actions = parseRuleActions(action.extension);
  const alwaysRun = parseAlwaysRun(action.extension);
  const conditionGroupOperator = parseConditionGroupOperator(action.extension);
  const isElseBranch = parseElseBranch(action.extension);

  return {
    id: action.id ?? makeEditorId('rung'),
    name: action.title ?? `Branch ${index + 1}`,
    description: action.description,
    enabled: action.condition?.[0]?.kind !== 'stop',
    alwaysRun,
    isElseBranch,
    conditionGroups,
    conditionGroupOperator,
    actions,
    priority: index,
  };
}

function parseElseBranch(extensions: Extension[] | undefined): boolean {
  return findExtension(extensions, RE_RUNG_ELSE_BRANCH_EXTENSION)?.valueBoolean === true;
}

function parseAlwaysRun(extensions: Extension[] | undefined): boolean {
  const ext = findExtension(extensions, `${RE_RUNG_CONDITION_EXTENSION}-always-run`);
  return ext?.valueBoolean === true;
}

function parseConditionGroupOperator(extensions: Extension[] | undefined): 'AND' | 'OR' | undefined {
  const ext = findExtension(extensions, `${RE_RUNG_CONDITION_EXTENSION}-group-operator`);
  if (ext?.valueString === 'OR') return 'OR';
  if (ext?.valueString === 'AND') return 'AND';
  return undefined;
}

function parseConditionGroups(extensions: Extension[] | undefined): ConditionGroup[] {
  const ext = findExtension(extensions, RE_RUNG_CONDITION_EXTENSION);
  if (!ext?.valueString) return [];
  try {
    const parsed = JSON.parse(ext.valueString);
    if (!Array.isArray(parsed)) return [];
    return parsed.map((g: Record<string, unknown>) => ({
      id: (g.id as string) ?? makeEditorId('cg'),
      groupOperator: (g.groupOperator as 'AND' | 'OR') ?? 'AND',
      conditions: Array.isArray(g.conditions)
        ? g.conditions.map((c: Record<string, unknown>) => ({
            id: (c.id as string) ?? makeEditorId('cond'),
            field: c.field as ConditionField,
            operator: c.operator as ConditionOperator,
            value: String(c.value ?? ''),
            logicalOperator: c.logicalOperator as 'AND' | 'OR' | undefined,
          }))
        : [],
    }));
  } catch {
    return [];
  }
}

function parseRuleActions(extensions: Extension[] | undefined): RuleAction[] {
  const ext = findExtension(extensions, RE_RUNG_ACTION_EXTENSION);
  if (!ext?.valueString) return [];
  try {
    const parsed = JSON.parse(ext.valueString);
    if (!Array.isArray(parsed)) return [];
    return parsed.map((a: Record<string, unknown>) => migrateActionFromStorage(a));
  } catch {
    return [];
  }
}

/** Parse and migrate a stored action object, handling legacy fields */
function migrateActionFromStorage(a: Record<string, unknown>): RuleAction {
  const type = (a.type as ActionType) ?? 'send-notification';

  // Migrate legacy email recipientPolicies to notificationRecipientPolicies
  let notificationRecipientPolicies = a.notificationRecipientPolicies as RecipientPolicy[] | undefined;
  if (!notificationRecipientPolicies) {
    const legacyPolicies = a.recipientPolicies as RecipientPolicy[] | undefined;
    const legacyPolicy = a.recipientPolicy as RecipientPolicy | undefined;
    if (legacyPolicies) notificationRecipientPolicies = legacyPolicies;
    else if (legacyPolicy) notificationRecipientPolicies = [legacyPolicy];
  }
  notificationRecipientPolicies = notificationRecipientPolicies?.map(normalizeRecipientPolicy);

  const emailRecipientPolicies = (a.emailRecipientPolicies as RecipientPolicy[] | undefined)?.map(
    normalizeRecipientPolicy,
  );
  const assignmentRecipientPolicies = (a.assignmentRecipientPolicies as RecipientPolicy[] | undefined)?.map(
    normalizeRecipientPolicy,
  );

  // Migrate legacy action keys
  let actionKey = a.actionKey as RuleActionKey | undefined;
  if (actionKey && (actionKey as string).startsWith('send_email.')) {
    actionKey = (actionKey as string).replace('send_email.', 'notify.') as RuleActionKey;
  }

  return {
    id: (a.id as string) ?? makeEditorId('act'),
    type,
    label: String(a.label ?? ''),
    description: a.description as string | undefined,
    actionKey,
    notificationRecipientPolicies,
    notificationTitle: a.notificationTitle as string | undefined,
    notificationBody: a.notificationBody as string | undefined,
    emailRecipientPolicies,
    emailTemplateRef: a.emailTemplateRef as TemplateLibraryRef | undefined,
    formTemplateRefs: a.formTemplateRefs as TemplateLibraryRef[] | undefined,
    assignmentRecipientPolicies,
    assignmentDueOffsetDays: typeof a.assignmentDueOffsetDays === 'number' ? a.assignmentDueOffsetDays : undefined,
    assignmentMessageTemplateRef: a.assignmentMessageTemplateRef as TemplateLibraryRef | undefined,
    claimFieldPath: a.claimFieldPath as string | undefined,
    claimFieldValue: a.claimFieldValue as string | undefined,
    claimFieldSystem: a.claimFieldSystem as string | undefined,
    claimFieldValueSource:
      a.claimFieldValueSource === 'graph' ? 'graph' : undefined,
    dedupeWindow: a.dedupeWindow as string | undefined,
    timingBucket: a.timingBucket as TimingBucket | undefined,
    scheduledTiming: a.scheduledTiming as ActionScheduledTiming | undefined,
    scheduleIdentity: a.scheduleIdentity as string | undefined,
  };
}

// ---------------------------------------------------------------------------
// RulePackDraft → PlanDefinition
// ---------------------------------------------------------------------------

export function rulePackDraftToPlanDefinition(
  draft: RulePackDraft,
  opts: { id?: string; status?: string; version?: string },
): PlanDefinition {
  const stableKey = draft.stableRuleKey || generateStableRuleKey();
  const canonicalUrl = `${RE_CANONICAL_URL_PREFIX}${stableKey}`;

  const extensions: Extension[] = [
    { url: RE_DOMAIN_EXTENSION, valueString: draft.domain },
    { url: RE_CONFLICT_POLICY_EXTENSION, valueString: draft.conflictPolicy },
    { url: RE_STABLE_RULE_KEY_EXTENSION, valueString: stableKey },
    { url: RE_V1_MARKER_EXTENSION, valueBoolean: true },
    { url: RE_SCHEMA_VERSION_EXTENSION, valueInteger: CURRENT_SCHEMA_VERSION },
  ];
  const triggerExt = buildTriggerExtension(draft.trigger);
  if (triggerExt.extension && triggerExt.extension.length > 0) {
    extensions.push(triggerExt);
  }
  const scopeExt = buildScopeExtension(draft.scope);
  if (scopeExt.extension && scopeExt.extension.length > 0) {
    extensions.push(scopeExt);
  }
  if (draft.steps && draft.steps.length > 0) {
    extensions.push({
      url: RE_WORKFLOW_STEPS_EXTENSION,
      valueString: JSON.stringify(draft.steps),
    });
  }

  // Prefer workflow steps as source of truth when present (matches runtime load path).
  const rungsForPersistence =
    draft.steps && draft.steps.length > 0 ? workflowStepsToRungs(draft.steps) : draft.rungs;
  const enabledRungs = rungsForPersistence.filter((rung) => rung.enabled !== false);

  const actions: PlanDefinitionAction[] = enabledRungs.map((rung) => {
    const conditionGroups = rung.alwaysRun ? [] : rung.conditionGroups;

    const rungExtensions: Extension[] = [
      {
        url: RE_RUNG_CONDITION_EXTENSION,
        valueString: JSON.stringify(conditionGroups),
      },
      {
        url: RE_RUNG_ACTION_EXTENSION,
        valueString: JSON.stringify(serializeActionsForStorage(rung.actions)),
      },
    ];

    if (!rung.alwaysRun && rung.conditionGroupOperator && rung.conditionGroupOperator !== 'AND') {
      rungExtensions.push({
        url: `${RE_RUNG_CONDITION_EXTENSION}-group-operator`,
        valueString: rung.conditionGroupOperator,
      });
    }

    // Still write alwaysRun for backward compat with V1 readers
    if (rung.alwaysRun) {
      rungExtensions.push({
        url: `${RE_RUNG_CONDITION_EXTENSION}-always-run`,
        valueBoolean: true,
      });
    }
    if (rung.isElseBranch) {
      rungExtensions.push({
        url: RE_RUNG_ELSE_BRANCH_EXTENSION,
        valueBoolean: true,
      });
    }

    return {
      id: rung.id,
      title: rung.name,
      description: rung.description,
      extension: rungExtensions,
    };
  });

  return {
    resourceType: 'PlanDefinition',
    ...(opts.id ? { id: opts.id } : draft.id ? { id: draft.id } : {}),
    url: canonicalUrl,
    title: draft.title,
    description: draft.description,
    status: (opts.status ?? draft.status) as PlanDefinition['status'],
    version: opts.version ?? draft.version,
    extension: extensions,
    action: actions,
  };
}

/** Serialize actions for storage, omitting deprecated fields */
function serializeActionsForStorage(actions: RuleAction[]): Record<string, unknown>[] {
  return actions.map((a) => {
    const timingBucket: TimingBucket = a.timingBucket === 'scheduled' ? 'scheduled' : 'immediate';
    const stored: Record<string, unknown> = {
      id: a.id,
      type: a.type,
      label: a.label,
      description: a.description,
      timingBucket,
      dedupeWindow: a.dedupeWindow,
    };
    if (a.scheduledTiming) {
      stored.scheduledTiming = a.scheduledTiming;
    }
    if (a.scheduleIdentity) {
      stored.scheduleIdentity = a.scheduleIdentity;
    }
    // Only write actionKey if present (not required for V2 rules)
    if (a.actionKey) {
      stored.actionKey = a.actionKey;
    }

    if (a.type === 'send-notification') {
      stored.notificationRecipientPolicies = a.notificationRecipientPolicies;
      if (a.notificationTitle) stored.notificationTitle = a.notificationTitle;
      if (a.notificationBody) stored.notificationBody = a.notificationBody;
    }

    if (a.type === 'send-email') {
      stored.emailRecipientPolicies = a.emailRecipientPolicies;
      stored.emailTemplateRef = a.emailTemplateRef;
    }

    if (a.type === 'assign-template-forms') {
      stored.formTemplateRefs = a.formTemplateRefs;
      stored.assignmentRecipientPolicies = a.assignmentRecipientPolicies;
      stored.assignmentDueOffsetDays = a.assignmentDueOffsetDays;
      stored.assignmentMessageTemplateRef = a.assignmentMessageTemplateRef;
    }

    if (a.type === 'set-claim-field') {
      stored.claimFieldPath = a.claimFieldPath;
      stored.claimFieldValue = a.claimFieldValue;
      if (a.claimFieldSystem) stored.claimFieldSystem = a.claimFieldSystem;
      if (a.claimFieldValueSource === 'graph') stored.claimFieldValueSource = 'graph';
    }

    return stored;
  });
}

function buildTriggerExtension(trigger: TriggerConfig): Extension {
  const sub: Extension[] = [
    ...trigger.resourceTypes.map((rt) => ({ url: 'resourceType', valueString: rt })),
    ...trigger.eventKinds.map((ek) => ({ url: 'eventKind', valueString: ek })),
    { url: 'mode', valueString: trigger.mode },
    { url: 'manualRunEligible', valueBoolean: trigger.manualRunEligible },
    { url: 'sideEffectsAllowed', valueBoolean: trigger.sideEffectsAllowed },
  ];
  if (trigger.objectFilters.length > 0) {
    sub.push({ url: 'objectFilters', valueString: JSON.stringify(trigger.objectFilters) });
  }
  // V2: repeat operation extensions for multi-operation triggers. Existing
  // drafts with only the scalar field serialize exactly as before.
  const operations = trigger.operations?.length ? trigger.operations : (trigger.operation ? [trigger.operation] : []);
  sub.push(...operations.map((operation) => ({ url: 'operation', valueString: operation })));
  if (trigger.filters && trigger.filters.length > 0) {
    sub.push({ url: 'filters', valueString: JSON.stringify(trigger.filters) });
  }
  return { url: RE_TRIGGER_EXTENSION, extension: sub };
}

function buildScopeExtension(scope: ScopeConfig): Extension {
  const sub: Extension[] = [
    ...scope.practiceIds.map((id) => ({ url: 'practiceId', valueString: id })),
  ];
  if (scope.effectiveDateStart) sub.push({ url: 'effectiveDateStart', valueString: scope.effectiveDateStart });
  if (scope.effectiveDateEnd) sub.push({ url: 'effectiveDateEnd', valueString: scope.effectiveDateEnd });
  return { url: RE_SCOPE_EXTENSION, extension: sub };
}

// ---------------------------------------------------------------------------
// PlanDefinition → RulePackListItem
// ---------------------------------------------------------------------------

export function planDefinitionToListItem(pd: PlanDefinition): RulePackListItem {
  const rawDomain = getExtString(pd.extension, RE_DOMAIN_EXTENSION);
  const domain = migrateDomain(rawDomain);
  const triggerExt = findExtension(pd.extension, RE_TRIGGER_EXTENSION);
  const mode = (triggerExt?.extension ? getExtString(triggerExt.extension, 'mode') : 'real-time') as TriggerMode;
  const triggerResources = triggerExt?.extension
    ? getExtStringArray(triggerExt.extension, 'resourceType') as TriggerResourceType[]
    : [];
  const triggerOperation = (triggerExt?.extension
    ? getExtString(triggerExt.extension, 'operation')
    : undefined) as TriggerOperation | undefined;

  return {
    id: pd.id ?? '',
    title: pd.title ?? 'Untitled',
    description: pd.description ?? '',
    version: pd.version ?? '0',
    status: (pd.status as RulePackStatus) || 'draft',
    domain,
    rungCount: pd.action?.length ?? 0,
    lastUpdated: pd.meta?.lastUpdated ?? '',
    triggerMode: mode,
    triggerResources,
    triggerOperation,
  };
}

// ---------------------------------------------------------------------------
// Empty draft
// ---------------------------------------------------------------------------

export function emptyRulePackDraft(): RulePackDraft {
  return {
    stableRuleKey: generateStableRuleKey(),
    title: '',
    description: '',
    status: 'draft',
    domain: 'client-events',
    trigger: {
      resourceTypes: [],
      eventKinds: [],
      operations: ['created'],
      operation: 'created',
      filters: [],
      mode: 'real-time',
      manualRunEligible: true,
      sideEffectsAllowed: false,
      objectFilters: [],
    },
    scope: {
      practiceIds: [],
    },
    conflictPolicy: 'all-match',
    rungs: [],
    steps: [],
    schemaVersion: CURRENT_SCHEMA_VERSION,
  };
}

export function nextPublishedVersion(current: string | undefined): string {
  const num = parseInt(current ?? '0', 10) || 0;
  return String(num + 1);
}

/**
 * Find the highest version number for a given stable rule key
 * across all packs, for proper version incrementing.
 */
export function findHighestVersionForRuleKey(
  packs: PlanDefinition[],
  stableRuleKey: string,
): string | undefined {
  let highest: number | undefined;
  for (const pd of packs) {
    const key = getExtString(pd.extension, RE_STABLE_RULE_KEY_EXTENSION);
    if (key === stableRuleKey && pd.version) {
      const v = parseInt(pd.version, 10);
      if (!isNaN(v) && (highest === undefined || v > highest)) {
        highest = v;
      }
    }
  }
  return highest !== undefined ? String(highest) : undefined;
}

/**
 * Active PlanDefinitions with the same stable rule key as a newly published pack,
 * excluding the new resource (`keepResourceId`). Used to retire superseded actives
 * after immutable publish.
 */
export function findSupersededActiveRulePacks(
  packs: PlanDefinition[],
  stableRuleKey: string,
  keepResourceId: string,
): PlanDefinition[] {
  return packs.filter((pd) => {
    if (!pd.id || pd.id === keepResourceId) return false;
    if (pd.status !== 'active') return false;
    const key = getExtString(pd.extension, RE_STABLE_RULE_KEY_EXTENSION);
    return key === stableRuleKey;
  });
}

// ---------------------------------------------------------------------------
// WorkflowStep <-> LadderRung conversion (editor tree model)
// ---------------------------------------------------------------------------

/**
 * Convert flat `LadderRung[]` from draft into a single top-level
 * `WorkflowStep` of kind `branch`. If rungs is empty, returns `[]`.
 * A single always-run rung is treated as a passthrough (actions only, no branch split).
 */
export function rungsToWorkflowSteps(rungs: LadderRung[]): WorkflowStep[] {
  if (rungs.length === 0) return [];

  const isSingleAlwaysRun =
    rungs.length === 1 &&
    (rungs[0].alwaysRun ||
      rungs[0].conditionGroups.every(
        (g) => g.conditions.length === 0 || g.conditions.every((c) => !c.field && !c.value),
      ));

  if (isSingleAlwaysRun) {
    return rungs[0].actions.map((a) => ({
      id: makeEditorId('ws'),
      kind: 'action' as const,
      action: a,
    }));
  }

  const branches: WorkflowBranch[] = rungs.map((rung, i) => ({
    id: rung.id,
    name: rung.name,
    enabled: true,
    alwaysRun: rung.alwaysRun,
    conditionGroups: rung.conditionGroups,
    conditionGroupOperator: rung.conditionGroupOperator,
    steps: rung.actions.map((a) => ({
      id: makeEditorId('ws'),
      kind: 'action' as const,
      action: a,
    })),
    priority: i,
  }));

  return [
    {
      id: makeEditorId('ws'),
      kind: 'branch' as const,
      branches,
      elseSteps: [],
    },
  ];
}

/**
 * Flatten a `WorkflowStep[]` tree back to `LadderRung[]` for persistence.
 * Handles nested branches by inlining them into the flat rung model
 * (nested branches are serialized in a `_nestedSteps` extension on the action).
 */
export function workflowStepsToRungs(steps: WorkflowStep[]): LadderRung[] {
  if (steps.length === 0) return [];

  const rungs: LadderRung[] = [];
  let priority = 0;

  // Leading actions before the first branch split (e.g. immediate notification).
  const firstBranchIdx = steps.findIndex((s) => s.kind === 'branch');
  const leadingEnd = firstBranchIdx === -1 ? steps.length : firstBranchIdx;
  const leadingActions = steps
    .slice(0, leadingEnd)
    .filter((s): s is Extract<WorkflowStep, { kind: 'action' }> => s.kind === 'action')
    .map((s) => s.action);
  if (leadingActions.length > 0) {
    rungs.push({
      id: makeEditorId('rung'),
      name: '',
      enabled: true,
      alwaysRun: true,
      conditionGroups: [],
      actions: leadingActions,
      priority: priority++,
    });
  }

  // All top-level actions and no branch — single passthrough rung (unless already added).
  if (firstBranchIdx === -1) {
    if (rungs.length === 0 && leadingActions.length > 0) {
      return rungs;
    }
    if (rungs.length > 0) return rungs;
    return [];
  }

  const branchStep = steps[firstBranchIdx] as Extract<WorkflowStep, { kind: 'branch' }>;

  for (const branch of branchStep.branches) {
    rungs.push({
      id: branch.id,
      name: branch.name,
      description: undefined,
      enabled: true,
      alwaysRun: branch.alwaysRun,
      conditionGroups: branch.alwaysRun ? [] : branch.conditionGroups,
      conditionGroupOperator: branch.conditionGroupOperator,
      actions: flattenStepsToActions(branch.steps),
      priority: priority++,
    });
  }

  const elseActions = flattenStepsToActions(branchStep.elseSteps);
  if (elseActions.length > 0) {
    rungs.push({
      id: makeEditorId('rung'),
      name: 'Else',
      enabled: true,
      alwaysRun: false,
      isElseBranch: true,
      conditionGroups: [],
      actions: elseActions,
      priority: priority++,
    });
  }

  return rungs;
}

function flattenStepsToActions(steps: WorkflowStep[]): RuleAction[] {
  const actions: RuleAction[] = [];
  for (const step of steps) {
    if (step.kind === 'action') {
      actions.push(step.action);
    }
    // Nested branches are editor-only for now; their actions are not
    // flattened into the parent rung to avoid semantic confusion.
  }
  return actions;
}

// ---------------------------------------------------------------------------
// Extension helpers (exported for reuse in validation/executor)
// ---------------------------------------------------------------------------

export function findExtension(exts: Extension[] | undefined, url: string): Extension | undefined {
  return exts?.find((e) => e.url === url);
}

export function getExtString(exts: Extension[] | undefined, url: string): string | undefined {
  return findExtension(exts, url)?.valueString;
}

export function getExtStringArray(exts: Extension[] | undefined, url: string): string[] {
  return (exts ?? []).filter((e) => e.url === url).map((e) => e.valueString ?? '').filter(Boolean);
}

export function getExtBoolean(exts: Extension[] | undefined, url: string): boolean | undefined {
  return findExtension(exts, url)?.valueBoolean;
}

export function getExtNumber(exts: Extension[] | undefined, url: string): number | undefined {
  return findExtension(exts, url)?.valueInteger;
}

// ---------------------------------------------------------------------------
// Deduplicate PlanDefinitions for overview — show only latest version per rule
// ---------------------------------------------------------------------------

/**
 * Given a list of Rules Engine PlanDefinitions, return only the newest
 * version for each stable rule key. This ensures the overview list does
 * not show both an older retired version and a newer active version
 * of the same rule.
 *
 * Grouping precedence:
 *   1. Stable rule key extension (preferred)
 *   2. PlanDefinition.url (canonical URL fallback)
 *   3. PlanDefinition.id (last resort)
 *
 * Within each group, the entry with the highest version wins.
 * If versions are equal or missing, `meta.lastUpdated` is the tie-breaker.
 *
 * Returns results sorted by most recently updated first.
 */
export function dedupeLatestRuleVersionsForOverview(plans: PlanDefinition[]): PlanDefinition[] {
  const byKey = new Map<string, PlanDefinition>();
  const noKey: PlanDefinition[] = [];

  for (const pd of plans) {
    const stableKey = getExtString(pd.extension, RE_STABLE_RULE_KEY_EXTENSION);
    const groupingKey = stableKey ?? pd.url ?? undefined;

    if (!groupingKey) {
      noKey.push(pd);
      continue;
    }

    const existing = byKey.get(groupingKey);
    if (!existing) {
      byKey.set(groupingKey, pd);
      continue;
    }

    const versionCmp = compareVersionsForOverview(pd.version, existing.version);
    if (versionCmp > 0) {
      byKey.set(groupingKey, pd);
    } else if (versionCmp === 0) {
      const pdUpdated = pd.meta?.lastUpdated ?? '';
      const existingUpdated = existing.meta?.lastUpdated ?? '';
      if (pdUpdated > existingUpdated) {
        byKey.set(groupingKey, pd);
      }
    }
  }

  const result = [...byKey.values(), ...noKey];
  result.sort((a, b) => {
    const aDate = a.meta?.lastUpdated ?? '';
    const bDate = b.meta?.lastUpdated ?? '';
    return bDate.localeCompare(aDate);
  });
  return result;
}

function compareVersionsForOverview(a: string | undefined, b: string | undefined): number {
  const partsA = (a ?? '0').split('.').map((s) => parseInt(s, 10) || 0);
  const partsB = (b ?? '0').split('.').map((s) => parseInt(s, 10) || 0);
  const len = Math.max(partsA.length, partsB.length);
  for (let i = 0; i < len; i++) {
    const diff = (partsA[i] ?? 0) - (partsB[i] ?? 0);
    if (diff !== 0) return diff;
  }
  return 0;
}
